import { useCallback, useMemo } from 'react';
import { useApiClient } from 'hooks/useApiClient';
import { API_ROUTES } from 'utils/constants';
import {
  Expense,
  CreateExpenseRequest,
  UpdateExpenseDeductibilityRequest,
} from 'types/expenses';

/**
 * Expenses API service
 * Consolidates all expense-related API calls in a single location
 */
export function useExpensesService() {
  const apiClient = useApiClient();

  /**
   * Get all expenses
   * @param includeVoided - Optional flag to include voided expenses (default: false)
   */
  const getAllExpenses = useCallback(
    async (includeVoided = false): Promise<Expense[]> => {
      const queryParams: Record<string, string> = {};
      if (includeVoided) {
        queryParams.includeVoided = 'true';
      }
      return apiClient.get<Expense[]>(API_ROUTES.EXPENSES, queryParams);
    },
    [apiClient],
  );

  /**
   * Get a single expense by ID
   */
  const getExpenseById = useCallback(
    async (id: string): Promise<Expense> => {
      return apiClient.get<Expense>(`${API_ROUTES.EXPENSES}/${id}`);
    },
    [apiClient],
  );

  /**
   * Create a new expense
   */
  const createExpense = useCallback(
    async (expense: CreateExpenseRequest): Promise<Expense> => {
      return apiClient.post<Expense>(
        API_ROUTES.EXPENSES,
        expense as unknown as Record<string, unknown>,
      );
    },
    [apiClient],
  );

  /**
   * Void an expense
   */
  const voidExpense = useCallback(
    async (id: string, reason?: string): Promise<Expense> => {
      const body = reason ? { reason } : undefined;
      const result = await apiClient.delete<Expense>(
        `${API_ROUTES.EXPENSES}/${id}`,
        body as unknown as Record<string, unknown>,
      );
      if (!result) {
        throw new Error('Failed to void expense: No response from server');
      }
      return result;
    },
    [apiClient],
  );

  const updateExpenseDeductibility = useCallback(
    async (
      id: string,
      body: UpdateExpenseDeductibilityRequest,
    ): Promise<Expense> => {
      return apiClient.patch<Expense>(
        `${API_ROUTES.EXPENSES}/${id}/deductibility`,
        body as unknown as Record<string, unknown>,
      );
    },
    [apiClient],
  );

  return useMemo(
    () => ({
      getAllExpenses,
      getExpenseById,
      createExpense,
      voidExpense,
      updateExpenseDeductibility,
    }),
    [
      getAllExpenses,
      getExpenseById,
      createExpense,
      voidExpense,
      updateExpenseDeductibility,
    ],
  );
}
